import { useEffect, useState } from "react";
import { Share, Download, X, PlusSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const KEY = "hc_install_dismissed"; // per-session UI flag only — never PHI or auth data
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isMobile = () => window.matchMedia("(max-width: 900px)").matches;

export function InstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem(KEY)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIos() && isMobile()) setShow(true);
    const onInstalled = () => { setShow(false); sessionStorage.setItem(KEY, "installed"); };
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (!user || user.is_demo || !show) return null;

  const dismiss = () => { sessionStorage.setItem(KEY, "1"); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") sessionStorage.setItem(KEY, "installed");
    setShow(false);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[380px] z-40 rounded-3xl bg-stone-900 text-stone-100 p-4 shadow-2xl shadow-stone-900/30" data-testid="install-prompt">
      <button onClick={dismiss} aria-label="Dismiss" data-testid="install-dismiss" className="absolute top-3 right-3 text-stone-400 hover:text-white"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-2xl shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold">Put Root & Rose on your home screen</div>
          {deferred ? (
            <>
              <p className="text-xs text-stone-400 mt-1">Opens full-screen like an app, with your reminders one tap away.</p>
              <button onClick={install} data-testid="install-btn" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-stone-900 text-sm font-bold hover:bg-amber-300">
                <Download className="w-4 h-4" /> Install
              </button>
            </>
          ) : (
            <p className="text-xs text-stone-300 mt-1 leading-relaxed" data-testid="install-ios-steps">
              In Safari tap <Share className="w-3.5 h-3.5 inline -mt-0.5 text-amber-300" /> <span className="font-semibold">Share</span>, then <PlusSquare className="w-3.5 h-3.5 inline -mt-0.5 text-amber-300" /> <span className="font-semibold">Add to Home Screen</span>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
